import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useDemoCtx } from './DemoContext';

const NAV = [
    { key: 'dashboard', ico: '🗺️', txt: '대시보드', path: '/demo/dashboard' },
    { key: 'sites', ico: '🌲', txt: '생육단지 관리', path: '/demo/sites' },
    {
        key: 'irrigation',
        ico: '💧',
        txt: '관수 관리',
        path: '/demo/irrigation',
        pill: '3',
    },
    { key: 'reports', ico: '📊', txt: '보고서', path: '/demo/reports' },
    { key: 'settings', ico: '⚙️', txt: '설정', path: '/demo/settings' },
];

const PAGE_TITLE: Record<string, { title: string; bc: string }> = {
    '/demo/dashboard': { title: '메인 대시보드', bc: '홈 › 대시보드' },
    '/demo/sites': { title: '생육단지 목록', bc: '홈 › 생육단지 관리 › 목록' },
    '/demo/irrigation': { title: '관수 관리', bc: '홈 › 관수 관리' },
    '/demo/reports': { title: '보고서', bc: '홈 › 보고서' },
    '/demo/sensors': { title: '센서 관리', bc: '홈 › 센서 관리' },
    '/demo/settings': { title: '설정', bc: '홈 › 설정' },
};

export default function DemoLayout() {
    const [collapsed, setCollapsed] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { toast, requestDrawingMode } = useDemoCtx();

    const path = location.pathname;
    const pageKey =
        Object.keys(PAGE_TITLE).find(k => path.startsWith(k)) ??
        '/demo/dashboard';
    const { title, bc } = PAGE_TITLE[pageKey] ?? {
        title: '대시보드',
        bc: '홈',
    };

    const bcParts = bc.split('›').map(p => p.trim());

    return (
        <div className="gmw gmw-app">
            {/* SIDEBAR */}
            <aside className={`sidebar${collapsed ? ' collapsed' : ''}`}>
                <div className="sb-logo">
                    <div className="sb-logo-mark">🌿</div>
                    <div className="sb-logo-text">
                        <em>스마트</em> TreeMap
                    </div>
                </div>
                <nav className="sb-nav">
                    <div className="sb-section-lbl">메인</div>
                    {NAV.slice(0, 1).map(n => (
                        <div
                            key={n.key}
                            className={`nav-item${path.startsWith(n.path) ? ' active' : ''}`}
                            onClick={() => navigate(n.path)}
                        >
                            <span className="nav-ico">{n.ico}</span>
                            <span className="nav-txt">{n.txt}</span>
                        </div>
                    ))}
                    <div className="sb-section-lbl" style={{ marginTop: 8 }}>
                        관리
                    </div>
                    {NAV.slice(1, 4).map(n => (
                        <div key={n.key}>
                            <div
                                className={`nav-item${path.startsWith(n.path) ? ' active' : ''}`}
                                onClick={() => navigate(n.path)}
                            >
                                <span className="nav-ico">{n.ico}</span>
                                <span className="nav-txt">{n.txt}</span>
                                {n.pill && (
                                    <span className="nav-pill">{n.pill}</span>
                                )}
                            </div>
                            {n.key === 'sites' && !collapsed && (
                                <div className="nav-sub">
                                    <div
                                        className={`nav-sub-item${path === '/demo/sites' ? ' active' : ''}`}
                                        onClick={() => navigate('/demo/sites')}
                                    >
                                        단지 목록
                                    </div>
                                    <div
                                        className="nav-sub-item"
                                        onClick={() => {
                                            requestDrawingMode();
                                            navigate('/demo/dashboard');
                                        }}
                                    >
                                        단지 등록
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                    <div className="sb-section-lbl" style={{ marginTop: 8 }}>
                        시스템
                    </div>
                    {NAV.slice(4).map(n => (
                        <div
                            key={n.key}
                            className={`nav-item${path.startsWith(n.path) ? ' active' : ''}`}
                            onClick={() => navigate(n.path)}
                        >
                            <span className="nav-ico">{n.ico}</span>
                            <span className="nav-txt">{n.txt}</span>
                        </div>
                    ))}
                </nav>
                <div className="sb-footer">
                    <div className="sb-user">
                        <div className="sb-avatar">김</div>
                        <div className="sb-user-info">
                            <div className="sb-user-name">김관리자</div>
                            <div className="sb-user-role">
                                관리자 · (주)그린조경
                            </div>
                        </div>
                    </div>
                    <button
                        className="sb-toggle"
                        onClick={() => setCollapsed(c => !c)}
                    >
                        {collapsed ? '▶' : '◀'}
                    </button>
                </div>
            </aside>

            {/* MAIN */}
            <div className="main-area">
                <header className="topbar">
                    <div>
                        <div className="topbar-title">{title}</div>
                        <div className="topbar-bc">
                            {bcParts.map((p, i) => (
                                <span key={i}>
                                    {i > 0 && <span className="sep"> › </span>}
                                    <span>{p}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="tb-search">
                        <span className="tb-search-ico">🔍</span>
                        <input
                            type="text"
                            className="tb-search-input"
                            placeholder="단지명, 주소, 수종 검색..."
                        />
                    </div>
                    <div className="tb-spacer" />
                    <div className="tb-actions">
                        <button
                            className="tb-icon-btn"
                            onClick={() => toast('새 알림 3건', 'warning')}
                        >
                            🔔
                            <div className="notif-dot" />
                        </button>
                        <button
                            className="tb-profile"
                            onClick={() => toast('프로필 메뉴', 'success')}
                        >
                            <div className="tb-profile-av">김</div>
                            <span className="tb-profile-name">김관리자</span>
                            <span
                                style={{ fontSize: 10, color: 'var(--text3)' }}
                            >
                                ▾
                            </span>
                        </button>
                        <button className="tb-lang">KO</button>
                    </div>
                </header>
                <div className="content">
                    <Outlet />
                </div>
            </div>
        </div>
    );
}
